import React, { createContext, useContext, useEffect, useState } from 'react';
import { defaultState } from './AppContextUtils';
import { AppContextProviderProps, AppContextState } from './AppContextTypes';
import { initializeFetchers } from '@/services/fetch';

const AppContext = createContext<AppContextState>(defaultState);

export function AppContextProvider({
  children,
  ...props
}: AppContextProviderProps & Record<string, unknown>): JSX.Element | null {
  const [initializingFetchers, setInitializingFetchers] = useState(true);
  const { logger, analytics, commit, dispatch, getters, adminAppRouter, triggerAdminAppToast, options, ...restProps } =
    props;

  const theme = options?.theme ?? 'goldcast';

  // commit/dispatch/getters arrive as raw Vue store props; cast at this interop boundary
  const adminAppStore = {
    commit,
    dispatch,
    getters,
    user: (getters as Record<string, unknown>)?.['user/getCurrentUser'],
    userCognitoDetails: (getters as Record<string, unknown>)?.['user/getCurrentUserCognitoDetails'],
    currentOrg: (getters as Record<string, unknown>)?.['org/getCurrentOrg'],
    orgPricingConfig: (getters as Record<string, unknown>)?.['orgPricingConfig/getPricingConfig']
  } as AppContextState['adminAppStore'];

  useEffect(() => {
    try {
      initializeFetchers(adminAppStore);
      setInitializingFetchers(false);
    } catch (e) {
      console.warn('Fetchers may already be initialized : ', e);
    }

    // Redirect to admin app if user is on content.goldcast.io
    if (document.location.href.includes('content.goldcast.io')) {
      document.location.href = 'https://admin.goldcast.io/content';
      return;
    }
  }, []);

  if (initializingFetchers) {
    return null;
  }

  return (
    <AppContext.Provider
      value={{
        theme,
        logger,
        analytics: {
          ...analytics,
          track: async (...args) => {
            if (analytics?.track) {
              await analytics.track(...args);
            }
            if (logger?.track) {
              logger.track(...args);
            }
          }
        },
        adminAppRouter,
        triggerAdminAppToast,
        adminAppStore
      }}
    >
      {React.cloneElement(children, {
        ...restProps
      })}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  return useContext(AppContext);
}
